import React, { useState, useRef, useEffect } from 'react';
import { Input } from '../InputDefault';
import IconProvider from '../IconProvider';

export interface MultiSelectOption {
  id: string;
  label: string;
  checked?: boolean;
}

interface MultiSelectDropdownWithButtonProps {
  title: string;
  options: MultiSelectOption[];
  selectedOptions: string[];
  onSelectionChange: (selectedIds: string[]) => void;
  placeholder?: string;
  className?: string;
  maxHeight?: string;
  triggerElement?: React.ReactNode;
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  position?: 'bottom' | 'top';
  buttonRect?: DOMRect | null;
}

export default function MultiSelectDropdownWithButton({
  title,
  options,
  selectedOptions,
  onSelectionChange,
  placeholder = "Search Tags ...",
  className = "",
  maxHeight = "max-h-60",
  triggerElement,
  isOpen: controlledIsOpen,
  onOpenChange,
  position = 'bottom',
  buttonRect
}: MultiSelectDropdownWithButtonProps) {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Use controlled or uncontrolled state
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
  const setIsOpen = (open: boolean) => {
    if (controlledIsOpen !== undefined) {
      onOpenChange?.(open);
    } else {
      setInternalIsOpen(open);
    }
  };

  // Filter options based on search term
  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle checkbox change
  const handleCheckboxChange = (optionId: string, checked: boolean) => {
    if (checked) {
      onSelectionChange([...selectedOptions, optionId]);
    } else {
      onSelectionChange(selectedOptions.filter(id => id !== optionId));
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      // Check if the click is on the trigger element
      const isOnTrigger = containerRef.current && containerRef.current.contains(target);
      
      // Check if the click is on the dropdown content (even when fixed positioned)
      const isOnDropdown = dropdownRef.current && dropdownRef.current.contains(target);
      
      // Check if the click is on any element with the dropdown's data attributes
      const isOnDropdownElement = (target as Element)?.closest('[data-component-file="MultiSelectDropdown.tsx"]');
      
      // Only close if click is not on any of these elements
      if (!isOnTrigger && !isOnDropdown && !isOnDropdownElement) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Clear search when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
    }
  }, [isOpen]);

  // Disable body scrolling when dropdown is open
  useEffect(() => {
    if (isOpen) {
      // Block scrolling while keeping scrollbars visible to prevent layout shifts
      const scrollableElements: HTMLElement[] = [];
      
      // Add body to the list
      scrollableElements.push(document.body);
      
      // Find all scrollable parent containers
      let currentElement = containerRef.current?.parentElement;
      while (currentElement) {
        const computedStyle = window.getComputedStyle(currentElement);
        const overflow = computedStyle.overflow;
        const overflowY = computedStyle.overflowY;
        
        // Check if element is scrollable
        if (overflow === 'auto' || overflow === 'scroll' || 
            overflowY === 'auto' || overflowY === 'scroll') {
          scrollableElements.push(currentElement);
        }
        
        currentElement = currentElement.parentElement;
      }
      
      // Block scrolling on all found elements while keeping scrollbars visible
      scrollableElements.forEach(element => {
        // Store original styles for restoration
        element.dataset.originalOverflow = element.style.overflow;
        element.dataset.originalOverflowY = element.style.overflowY;
        
        // Keep scrollbars visible but block scrolling
        element.style.overflow = 'auto';
        element.style.overflowY = 'auto';
        
        // Add scroll event listener to prevent scrolling
        const preventScroll = (e: Event) => e.preventDefault();
        element.addEventListener('wheel', preventScroll, { passive: false });
        element.addEventListener('touchmove', preventScroll, { passive: false });
        element.addEventListener('keydown', (e) => {
          // Only prevent default if the target is not an input field
          const target = e.target as HTMLElement;
          const isInputField = target.tagName === 'INPUT' || 
                              target.tagName === 'TEXTAREA' || 
                              target.contentEditable === 'true' ||
                              target.closest('input, textarea, [contenteditable="true"]');
          
          if (!isInputField && (e.key === 'ArrowUp' || e.key === 'ArrowDown' || 
              e.key === 'PageUp' || e.key === 'PageDown' || 
              e.key === 'Home' || e.key === 'End' || e.key === ' ')) {
            e.preventDefault();
          }
        });
        
        // Store the event listeners for cleanup
        (element as any).__dropdownScrollListeners = {
          wheel: preventScroll,
          touchmove: preventScroll
        };
      });
      
      // Store the elements for cleanup
      (window as any).__dropdownScrollElements = scrollableElements;
    } else {
      // Re-enable scrolling on all previously disabled elements
      const scrollableElements = (window as any).__dropdownScrollElements || [];
      scrollableElements.forEach((element: HTMLElement) => {
        if (element && element.style) {
          // Restore original styles
          element.style.overflow = element.dataset.originalOverflow || '';
          element.style.overflowY = element.dataset.originalOverflowY || '';
          
          // Remove event listeners
          const listeners = (element as any).__dropdownScrollListeners;
          if (listeners) {
            element.removeEventListener('wheel', listeners.wheel);
            element.removeEventListener('touchmove', listeners.touchmove);
            delete (element as any).__dropdownScrollListeners;
          }
          
          // Clean up data attributes
          delete element.dataset.originalOverflow;
          delete element.dataset.originalOverflowY;
        }
      });
      
      // Clear the stored elements
      delete (window as any).__dropdownScrollElements;
    }

    // Cleanup: re-enable scrolling when component unmounts
    return () => {
      const scrollableElements = (window as any).__dropdownScrollElements || [];
      scrollableElements.forEach((element: HTMLElement) => {
        if (element && element.style) {
          // Restore original styles
          element.style.overflow = element.dataset.originalOverflow || '';
          element.style.overflowY = element.dataset.originalOverflowY || '';
          
          // Remove event listeners
          const listeners = (element as any).__dropdownScrollListeners;
          if (listeners) {
            element.removeEventListener('wheel', listeners.wheel);
            element.removeEventListener('touchmove', listeners.touchmove);
            delete (element as any).__dropdownScrollListeners;
          }
          
          // Clean up data attributes
          delete element.dataset.originalOverflow;
          delete element.dataset.originalOverflowY;
        }
      });
      delete (window as any).__dropdownScrollElements;
    };
  }, [isOpen]);

  const dropdownContent = (
    <div 
      ref={dropdownRef}
      className={`
        bg-white border border-gray-200 rounded-lg shadow-lg p-4 min-w-64 z-50
        ${position === 'top' ? 'mb-2' : 'mt-2'}
      `}
      style={{ height: '300px' }}
    >
      {/* Title */}
      <div className="text-left mb-3">
        <h4 className="heading-4">{title}</h4>
      </div>

      {/* Search Bar */}
      <div className="relative mb-3">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <IconProvider 
            iconstring="fa-search" 
            size={16} 
            color="#9CA3AF" 
          />
        </div>
        <Input
          type="text"
          placeholder={placeholder}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 border-gray-300 focus:border-purple-500 focus:ring-purple-500"
        />
      </div>

      {/* Options List - Fixed height with scrolling */}
      <div className="overflow-y-auto" style={{ height: 'calc(300px - 120px)' }}>
        {filteredOptions.map((option) => {
          const isChecked = selectedOptions.includes(option.id);
          return (
            <div key={option.id} className="flex items-center mb-2">
              {/* Custom Checkbox */}
              <div className="relative">
                <input
                  type="checkbox"
                  id={`checkbox-${option.id}`}
                  checked={isChecked}
                  onChange={(e) => handleCheckboxChange(option.id, e.target.checked)}
                  className="sr-only"
                />
                <label
                  htmlFor={`checkbox-${option.id}`}
                  className={`
                    block w-5 h-5 border-2 rounded cursor-pointer transition-all duration-200
                    ${isChecked 
                      ? 'bg-purple-100 border-purple-500' 
                      : 'border-purple-500'
                    }
                  `}
                >
                  {/* Checkmark */}
                  {isChecked && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <svg
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-3 h-3"
                      >
                        <path
                          d="M3 8L7 12L13 4"
                          stroke="#6E35FF"
                          strokeWidth="2"
                          strokeLinecap="square"
                          strokeLinejoin="bevel"
                        />
                      </svg>
                    </div>
                  )}
                </label>
              </div>
              
              {/* Option Label */}
              <label
                htmlFor={`checkbox-${option.id}`}
                className="ml-3 text-sm font-medium text-gray-900 cursor-pointer flex-1 truncate"
              >
                {option.label}
              </label>
            </div>
          );
        })}
      </div>

      {/* No results message */}
      {filteredOptions.length === 0 && searchTerm && (
        <div className="text-center text-gray-500 text-sm py-4">
          No options found
        </div>
      )}
    </div>
  );

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {/* Trigger Element */}
      {triggerElement && (
        <div onClick={() => setIsOpen(!isOpen)}>
          {triggerElement}
        </div>
      )}

      {/* Dropdown Content - Only show when open */}
      {isOpen && buttonRect && (
        <div 
          className={`fixed z-50 w-64`}
          style={{
            left: (() => {
              // Ensure dropdown doesn't go off the right edge
              const dropdownWidth = 256; // w-64 = 16rem = 256px
              const rightEdge = buttonRect.left + dropdownWidth;
              if (rightEdge > window.innerWidth) {
                // Position to the left of the button instead
                return `${buttonRect.right - dropdownWidth}px`;
              }
              return `${buttonRect.left}px`;
            })(),
            top: (() => {
              // Calculate available space above and below
              const spaceAbove = buttonRect.top;
              const spaceBelow = window.innerHeight - buttonRect.bottom;
              const dropdownHeight = 300 ; // Fixed height+30 for the space
              const dropdownHeightWithSpace = 300 + 30; // Fixed height+30 for the space
              
              // Determine best position based on available space
              if (spaceBelow >= dropdownHeightWithSpace) {
                // Enough space below, position below button
                return `${buttonRect.bottom + 10}px`;
              } else if (spaceAbove >= dropdownHeightWithSpace) {
                // Enough space above, position above button with proper height calculation
                return `${buttonRect.top  - dropdownHeight - 10}px`; 
              } else {
                // Not enough space in either direction, find the best fit
                if (spaceBelow > spaceAbove) {
                  // More space below, position below but ensure it doesn't go off screen
                  const maxTop = window.innerHeight - dropdownHeight - 10;
                  return `${Math.max(10, Math.min(buttonRect.bottom + 10, maxTop))}px`;
                } else {
                  // More space above, position above but ensure it doesn't go off screen
                  // Calculate the top position accounting for dropdown height
                  const topPosition = buttonRect.top - dropdownHeight - 10;
                  return `${Math.max(10, topPosition)}px`;
                }
              }
            })()
          }}
        >
          {dropdownContent}
        </div>
      )}
    </div>
  );
} 